import { useEffect, type RefObject } from 'react';

import { selectionInsideHost } from './selection-toolbar-shared';
import type { EditorActivation } from './use-viewport-editor-activation';

/**
 * Wires the generic `'focus'` and `'selection'` pin reasons (see
 * `useViewportEditorActivation`) for an EditorJS-backed block: pins while
 * focus is inside `wrapperRef`'s element, and while a live (non-collapsed)
 * selection is confined entirely to it. Both are "the user is actively
 * interacting with this editor" signals that must hold the editor mounted
 * even if the block is pushed outside the observed viewport bounds (e.g. a
 * fast programmatic scroll while a palette is open elsewhere).
 *
 * `'menu'` (block/inline menu open) and `'modal'` (this block is the
 * current EditLink source) are wired separately by each caller, since
 * those signals come from component-local state that differs between the
 * paragraph cluster and the list block.
 */
export function useEditorInteractionPins(
  wrapperRef: RefObject<HTMLElement | null>,
  { pin, unpin }: Pick<EditorActivation, 'pin' | 'unpin'>,
): void {
  useEffect(() => {
    const element = wrapperRef.current;
    if (!element) return;

    const handleFocusIn = (): void => {
      pin('focus');
    };
    const handleFocusOut = (e: FocusEvent): void => {
      const next = e.relatedTarget as Node | null;
      // Focus moved to another element still inside this wrapper (e.g.
      // between two EditorJS blocks in the same cluster) — stay pinned.
      if (next && element.contains(next)) return;
      unpin('focus');
    };

    element.addEventListener('focusin', handleFocusIn);
    element.addEventListener('focusout', handleFocusOut);
    return () => {
      element.removeEventListener('focusin', handleFocusIn);
      element.removeEventListener('focusout', handleFocusOut);
    };
  }, [wrapperRef, pin, unpin]);

  useEffect(() => {
    const handleSelectionChange = (): void => {
      const element = wrapperRef.current;
      if (!element || !selectionInsideHost(element)) {
        unpin('selection');
        return;
      }
      pin('selection');
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', handleSelectionChange);
  }, [wrapperRef, pin, unpin]);
}
